// src/pages/providers/5_patientOverview/components/ProviderEditPatientForm.js
//
// Modal form that allows Support, Org. Admin, and assigned Clinician users
// to edit a patient's basic details (name, reference number, and email).
//
// Follows a multi-step UI pattern:
//   summary → edit-name / edit-reference / edit-email → confirm → success
//   summary → unlock-forms → success  (single-step confirm for form lock toggle)
//
// On submit, calls the `updateServiceUserDetails` Cloud Function which
// handles updating the patient's Auth displayName/email, their root Firestore
// fields, formData answers, clinician doc references, informant doc
// references, customFields.referenceNumber, and the patient summary.
// Email updates are only available before the patient has set up their
// password (hasSetOwnPassword === false) and automatically resend the
// welcome email to the corrected address.
//
// Changes will NOT update point-in-time records (audit logs, generated PDFs,
// subcollection history entries) — those capture the data at the time of
// the action and remain as historical snapshots.

import React, { useState, useEffect } from 'react';
import * as AlertDialog from '@radix-ui/react-alert-dialog';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';
import {
    XIcon,
    ChevronRightIcon,
    UserIcon,
    IdentificationIcon,
    MailIcon,
    ShieldExclamationIcon,
    LockOpenIcon,
    LockClosedIcon,
} from '@heroicons/react/outline';
import { CheckCircleIcon as CheckCircleSolid } from '@heroicons/react/solid';
import WarningPanel from '../../../../components/informantManagement/WarningPanel';
import InfoTooltip from '../../../../components/InfoTooltip';
import { functions } from '../../../../firebase';
import { httpsCallable } from 'firebase/functions';
import { sanitizeNameInput, sanitizeEmailInput } from '../../../../utils/inputSanitization';
import { useAuth } from '../../../../hooks/useAuth';
import ResetMfaFlow from '../../../../components/userManagement/ResetMfaFlow';


function ProviderEditPatientForm({ isOpen, onClose, patient, patientId, onSuccess }) {
    // Navigation state: which screen is currently displayed
    // 'summary'        — overview of current patient details with action cards
    // 'edit-name'      — form to edit first name and last name
    // 'edit-reference'  — form to edit reference number
    // 'confirm'        — review changes before submitting
    // 'success'        — operation completed successfully
    const [currentStep, setCurrentStep] = useState('summary');

    // Tracks which edit flow led to the confirm screen so the "Back" button
    // returns to the correct step rather than always going to summary.
    const [editOrigin, setEditOrigin] = useState(null);

    // Edit form state — pre-populated from the patient's current data
    const [editData, setEditData] = useState({});

    // Async operation state
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState('');

    // Stores the action that was just completed by the unlock/re-lock flow.
    // Needed because the success screen cannot derive the action from the live
    // patient data — onSuccess() triggers a data refresh, so by the time the
    // success screen renders, patient.formsUnlocked may already reflect the
    // new value, making `!isCurrentlyUnlocked` produce the wrong answer.
    // Set at submit time; cleared when returning to summary.
    const [lastUnlockAction, setLastUnlockAction] = useState(null); // 'unlock' | 'relock' | null

    // MFA reset state — managed independently from the edit flow's
    // isSubmitting/submitError because the reset is a standalone action
    // that calls a different Cloud Function.
    const [mfaResetLoading, setMfaResetLoading] = useState(false);
    const [mfaResetError, setMfaResetError] = useState(null);

    // Auth context — used to gate the Account Management section to
    // Org. Admin and Support users only.
    const { isOrgAdmin, isSupportUser } = useAuth();

    // Track the patient ID we initialised for, so we only reset form state
    // when the modal opens fresh or switches to a different patient — NOT
    // when the same patient's data refreshes after a successful edit (which
    // would tear away the success screen before the user can read it).
    const [initialisedForPatientId, setInitialisedForPatientId] = useState(null);

    useEffect(() => {
        if (isOpen && patient) {
            const incomingId = patientId || patient?.uid;
            const isNewSession = initialisedForPatientId !== incomingId;

            if (isNewSession) {
                setCurrentStep('summary');
                setEditOrigin(null);
                setInitialisedForPatientId(incomingId);
            }

            // Always sync editData with the latest patient values so
            // subsequent edits within the same session reflect any
            // changes that were just saved.
            setEditData({
                firstName: patient.firstName || '',
                lastName: patient.lastName || '',
                referenceNumber: patient?.customFields?.referenceNumber || '',
                email: patient?.email || '',
            });
            setIsSubmitting(false);
            setSubmitError('');
        }

        if (!isOpen) {
            setInitialisedForPatientId(null);
        }
    }, [isOpen, patient, patientId, initialisedForPatientId]);

    // ─── Helpers ──────────────────────────────────────────────────────────────

    // Build an object containing only the fields that differ from the original
    // patient data. Returns null if nothing changed. When scope is provided,
    // only fields in that array are compared (used by individual edit screens
    // to check whether their specific fields changed).
    const computeChangedFields = (scope) => {
        if (!patient) return null;

        const changes = {};
        const original = {
            firstName: patient.firstName || '',
            lastName: patient.lastName || '',
            referenceNumber: patient?.customFields?.referenceNumber || '',
            email: patient?.email || '',
        };

        const keysToCheck = scope || Object.keys(original);

        for (const key of keysToCheck) {
            const editVal = (editData[key] || '').trim();
            const origVal = (original[key] || '').trim();
            // Email comparison is case-insensitive since the backend normalizes
            if (key === 'email') {
                if (editVal.toLowerCase() !== origVal.toLowerCase()) {
                    changes[key] = editData[key];
                }
            } else if (editVal !== origVal) {
                changes[key] = editData[key];
            }
        }

        return Object.keys(changes).length > 0 ? changes : null;
    };

    const FIELD_LABELS = {
        firstName: 'First Name',
        lastName: 'Last Name',
        referenceNumber: 'Reference Number',
        email: 'Email Address',
    };

    const getOriginalValue = (key) => {
        if (key === 'referenceNumber') return patient?.customFields?.referenceNumber || '(none)';
        return patient?.[key] || '';
    };

    // ─── Form validation ─────────────────────────────────────────────────────

    // Both firstName and lastName are required (non-empty after trim).
    const isNameFormValid = () => {
        return editData.firstName?.trim() && editData.lastName?.trim();
    };

    // Reference number must be non-empty (clearing is not allowed once set).
    const isReferenceFormValid = () => {
        return editData.referenceNumber?.trim()?.length > 0;
    };

    // Email must be non-empty and pass the same five structural guards
    // applied across the codebase (trailing dots, consecutive dots, double @,
    // etc.).  Mirrors validate_email_format (clinician_email_update.py) and
    // the hardened validateEmail in informantValidation.js / patientSetupValidation.js
    // so users see an inline form error instead of a server 500 from Firebase Auth.
    const isEmailFormValid = () => {
        const val = editData.email?.trim() || '';
        if (!val || val.length > 254) return false;
        if (/^[.,]/.test(val) || /[.,]$/.test(val)) return false;
        if (val.includes('..')) return false;
        if ((val.match(/@/g) || []).length !== 1) return false;
        const [local, domain] = val.split('@');
        if (local.endsWith('.') || domain.startsWith('.')) return false;
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);
    };

    // ─── Submit handler ──────────────────────────────────────────────────────

    const handleSubmit = async () => {
        // Scope the submission to the fields relevant to the current edit
        // flow so that stale edits from a previous screen don't leak in.
        // Each action in the modal is a standalone operation.
        const scopeMap = {
            'edit-reference': ['referenceNumber'],
            'edit-email': ['email'],
        };
        const scope = scopeMap[editOrigin] || ['firstName', 'lastName'];
        const changes = computeChangedFields(scope);
        if (!changes) return;

        setIsSubmitting(true);
        setSubmitError('');

        try {
            const updateFn = httpsCallable(functions, 'updateServiceUserDetails');
            await updateFn({
                targetPatientId: patientId,
                updates: changes,
            });

            setCurrentStep('success');

            // Notify the parent to refresh patient data so the overview
            // reflects the updates immediately.
            if (onSuccess) {
                onSuccess();
            }
        } catch (err) {
            console.error('Error updating patient details:', err);
            const message =
                err?.message || 'An unexpected error occurred. Please try again.';
            setSubmitError(message);
        } finally {
            setIsSubmitting(false);
        }
    };

    // ─── Navigation helpers ──────────────────────────────────────────────────

    const goToEditName = () => setCurrentStep('edit-name');
    const goToEditReference = () => setCurrentStep('edit-reference');
    const goToEditEmail = () => setCurrentStep('edit-email');

    const goToConfirm = (origin) => {
        // Scope the change check to the fields relevant to the edit origin
        // so that stale changes from a different edit screen don't leak in.
        const scopeMap = {
            'edit-reference': ['referenceNumber'],
            'edit-email': ['email'],
        };
        const scope = scopeMap[origin] || ['firstName', 'lastName'];
        const changes = computeChangedFields(scope);
        if (changes) {
            setEditOrigin(origin);
            setCurrentStep('confirm');
        }
    };

    const goBackToSummary = () => {
        setCurrentStep('summary');
        setEditOrigin(null);
        setSubmitError('');
        setLastUnlockAction(null);
    };

    // CYP ADHD patients are children with no email address and no Firebase
    // Auth account (hasAuthAccount: false on the header), so the email edit
    // flow does not apply to them. The parent/carer's email lives on their
    // own informant record and is managed via Manage Informants. Pronouns are
    // captured in the parent/carer's questionnaire, not on the patient record,
    // so no pronoun field belongs here either.
    const isCypAdhd = patient?.assessmentModule === 'cypAdhd';

    // ─── Form lock state (Adult ADHD only) ───────────────────────────────────
    // Determines whether the "Unlock/Re-lock Questionnaire Forms" action card
    // should appear, and what state it should reflect. The formsUnlocked flag
    // is a top-level boolean on the patient document that overrides the default
    // lock triggered by CI/SSI report generation.
    //
    // The card is shown when:
    //   1. Patient is Adult ADHD (missing or 'adultAdhd' assessmentModule)
    //   2. A CI or SSI report has been generated (forms are in a lockable state)
    //      OR formsUnlocked is already true (so re-lock is available)
    //   3. The org does not have an org-level form lock exemption
    const isAdultAdhd = !patient?.assessmentModule
        || patient.assessmentModule === ''
        || patient.assessmentModule === 'adultAdhd';
    const reportsGenerated =
        patient?.dataModel?.['6_manageReports']?.ciReportGenerated === true
        || patient?.dataModel?.['6_manageReports']?.ssiReportGenerated === true;
    const isOrgFormLockExempt = patient?.organisation === 'Psychological Consultations';
    const isCurrentlyUnlocked = patient?.formsUnlocked === true;
    const showFormLockAction = isAdultAdhd
        && (reportsGenerated || isCurrentlyUnlocked)
        && !isOrgFormLockExempt;

    // ─── Unlock/re-lock forms submit handler ─────────────────────────────────
    // Calls the unlockPatientForms Cloud Function to toggle the formsUnlocked
    // flag on the patient document. The CF handles authorization, validation,
    // and writes an audit entry to the formLockHistory subcollection.
    const handleUnlockFormsSubmit = async () => {
        const willUnlock = !isCurrentlyUnlocked;
        setIsSubmitting(true);
        setSubmitError('');
        try {
            const unlockFn = httpsCallable(functions, 'unlockPatientForms');
            await unlockFn({
                patientId,
                unlock: willUnlock,
            });
            // Capture the action BEFORE triggering the data refresh so the
            // success screen always knows what we just did, regardless of
            // whether the patient prop has already updated.
            setLastUnlockAction(willUnlock ? 'unlock' : 'relock');
            setEditOrigin('unlock-forms');
            setCurrentStep('success');
            if (onSuccess) {
                onSuccess();
            }
        } catch (err) {
            console.error('Error toggling form lock:', err);
            const message =
                err?.message || 'An unexpected error occurred. Please try again.';
            setSubmitError(message);
        } finally {
            setIsSubmitting(false);
        }
    };

    // ─── Render: Summary screen ──────────────────────────────────────────────

    const renderSummary = () => (
        <div className="space-y-3">
            <h3 className="text-lg font-bold text-gray-900 pr-6">
                Edit Patient Details
            </h3>

            {/* Current details card */}
            <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
                <div className="flex items-center space-x-2 mb-2">
                    <div className="w-8 h-8 rounded-full bg-violet-100 flex items-center justify-center flex-shrink-0">
                        <UserIcon className="h-4 w-4 text-violet-700" />
                    </div>
                    <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-900 break-words">
                            {patient?.firstName} {patient?.lastName}
                        </p>
                        {/* CYP ADHD children have no email; skip the empty line */}
                        {patient?.email && (
                            <p className="text-xs text-gray-500 break-words">{patient.email}</p>
                        )}
                    </div>
                </div>
            </div>

            {/* Action cards */}
            <div className="bg-white border border-gray-200 rounded-md p-3">
                <h4 className="text-xs font-medium text-gray-900 mb-2">Actions</h4>
                <div className="space-y-2">
                    {/* Edit Name */}
                    <button
                        onClick={goToEditName}
                        className="w-full flex items-center justify-between p-2 text-left border border-gray-200 rounded-md hover:bg-violet-50 hover:border-violet-200 transition-colors"
                    >
                        <div className="flex items-center space-x-2">
                            <UserIcon className="h-4 w-4 text-violet-600" />
                            <div>
                                <p className="text-xs font-semibold text-gray-900">Edit Name</p>
                                <p className="text-xs text-gray-500">Update first or last name</p>
                            </div>
                        </div>
                        <ChevronRightIcon className="h-4 w-4 text-gray-400" />
                    </button>

                    {/* Update Email Address — clickable before the patient has
                        signed in, greyed out with a tooltip explanation after.
                        When disabled we render a <div> instead of a <button> so
                        that pointer events still reach the InfoTooltip trigger.
                        Not rendered at all for CYP ADHD: the child has no email
                        or login account, and the backend rejects email updates
                        for auth-less patients (hasAuthAccount: false). */}
                    {!isCypAdhd && (() => {
                        const emailDisabled = patient?.hasSetOwnPassword === true;
                        const sharedClasses = `w-full flex items-center justify-between p-2 text-left border rounded-md transition-colors ${
                            emailDisabled
                                ? 'border-gray-100 bg-gray-50 cursor-not-allowed opacity-60'
                                : 'border-gray-200 hover:bg-violet-50 hover:border-violet-200'
                        }`;
                        const inner = (
                            <>
                                <div className="flex items-center space-x-2 min-w-0">
                                    <MailIcon className={`h-4 w-4 flex-shrink-0 ${emailDisabled ? 'text-gray-400' : 'text-violet-600'}`} />
                                    <div className="min-w-0">
                                        <p className={`text-xs font-semibold ${emailDisabled ? 'text-gray-400' : 'text-gray-900'}`}>Edit Email Address</p>
                                        <p className={`text-xs break-words ${emailDisabled ? 'text-gray-400' : 'text-gray-500'}`}>
                                            {patient?.email
                                                ? `Update email from ${patient.email}`
                                                : 'No email set'}
                                        </p>
                                    </div>
                                </div>
                                {emailDisabled ? (
                                    <InfoTooltip
                                        content="The patient's email can only be updated before they have signed in and set up their password."
                                        side="left"
                                        maxWidth="220px"
                                    />
                                ) : (
                                    <ChevronRightIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                                )}
                            </>
                        );

                        return emailDisabled ? (
                            <div className={sharedClasses}>{inner}</div>
                        ) : (
                            <button onClick={goToEditEmail} className={sharedClasses}>{inner}</button>
                        );
                    })()}
                    {/* Edit Reference Number */}
                    <button
                        onClick={goToEditReference}
                        className="w-full flex items-center justify-between p-2 text-left border border-gray-200 rounded-md hover:bg-violet-50 hover:border-violet-200 transition-colors"
                    >
                        <div className="flex items-center space-x-2 min-w-0">
                            <IdentificationIcon className="h-4 w-4 text-violet-600 flex-shrink-0" />
                            <div className="min-w-0">
                                <p className="text-xs font-semibold text-gray-900">Edit Reference Number</p>
                                <p className="text-xs text-gray-500 break-words">
                                    {patient?.customFields?.referenceNumber
                                        ? `Update reference number from ${patient.customFields.referenceNumber}`
                                        : 'No reference number set'}
                                </p>
                            </div>
                        </div>
                        <ChevronRightIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                    </button>

                    {/* Unlock / Re-lock Questionnaire Forms — Adult ADHD only.
                        Shown when the assessment has started (CI/SSI generated) so
                        forms are in a lockable state, or when an override is already
                        active. Not shown for org-level exemptions where forms are
                        always editable. */}
                    {showFormLockAction && (
                        <button
                            onClick={() => {
                                setSubmitError('');
                                setCurrentStep('unlock-forms');
                            }}
                            className="w-full flex items-center justify-between p-2 text-left border border-gray-200 rounded-md hover:bg-violet-50 hover:border-violet-200 transition-colors"
                        >
                            <div className="flex items-center space-x-2 min-w-0">
                                {isCurrentlyUnlocked
                                    ? <LockClosedIcon className="h-4 w-4 text-violet-600 flex-shrink-0" />
                                    : <LockOpenIcon className="h-4 w-4 text-violet-600 flex-shrink-0" />
                                }
                                <div className="min-w-0">
                                    <p className="text-xs font-semibold text-gray-900">
                                        {isCurrentlyUnlocked ? 'Re-lock Questionnaire Forms' : 'Unlock Questionnaire Forms'}
                                    </p>
                                    <p className="text-xs text-gray-500">
                                        {isCurrentlyUnlocked
                                            ? 'Restore read-only questionnaire access'
                                            : 'Allow the patient to edit their questionnaires'}
                                    </p>
                                </div>
                            </div>
                            <ChevronRightIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                        </button>
                    )}
                </div>
            </div>

            {/* Account Management — visible to Org. Admin and Support only.
                Not rendered for CYP ADHD patients who have no Auth account.
                Auth is the source of truth for MFA status; the isMFASetUp
                Firestore flag is shown for informational context only. */}
            {(isOrgAdmin() || isSupportUser()) && !isCypAdhd && (
                <div className="bg-white border border-gray-200 rounded-md p-3">
                    <h4 className="text-xs font-medium text-gray-900 mb-2">Account Management</h4>
                    <div className="space-y-2">
                        <button
                            onClick={() => setCurrentStep('reset-mfa')}
                            className="w-full flex items-center justify-between p-2 text-left border border-gray-200 rounded-md hover:bg-violet-50 hover:border-violet-200 transition-colors"
                        >
                            <div className="flex items-center space-x-2 min-w-0">
                                <ShieldExclamationIcon className="h-4 w-4 flex-shrink-0 text-violet-600" />
                                <div className="min-w-0">
                                    <p className="text-xs font-semibold text-gray-900">Reset two-factor authentication</p>
                                    <p className="text-xs break-words text-gray-500">
                                        {patient?.mfaMethod === 'totp'
                                            ? 'Recorded method: Authenticator app'
                                            : patient?.mfaMethod === 'phone'
                                                ? 'Recorded method: SMS'
                                                : patient?.isMFASetUp === true
                                                    ? 'Recorded method: Set up (type not recorded)'
                                                    : 'No method recorded'}
                                    </p>
                                </div>
                            </div>
                            <ChevronRightIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                        </button>
                    </div>
                </div>
            )}

            {/* Info banner about propagation */}
            <WarningPanel variant="info">
                <ul className="list-disc pl-4 space-y-1">
                    <li>Changes will update patient details across the platform, including <strong>questionnaire answers</strong>, <strong>informant records</strong>, and <strong>clinician records</strong> (for name changes).</li>
                    <li>Point-in-time records (audit logs, generated documents) will <strong>not</strong> be changed.</li>
                    <li><strong>If any reports have already been finalised, they will need to be re-finalised</strong> to reflect the updated details.</li>
                </ul>
            </WarningPanel>
        </div>
    );

    // ─── Render: Edit Name form ──────────────────────────────────────────────

    const renderEditName = () => (
        <div className="space-y-3">
            <button
                onClick={goBackToSummary}
                className="text-xs text-violet-600 hover:text-violet-800 font-semibold"
            >
                &larr; Back to Summary
            </button>

            <h3 className="text-base font-semibold text-gray-900">Edit Name</h3>

            {/* Warning about widespread update */}
            <WarningPanel variant="warning">
                <ul className="list-disc pl-4 space-y-1">
                    <li>This will update the patient's name across the platform, including their <strong>questionnaire answers</strong>, <strong>informant records</strong>, and <strong>clinician records</strong>.</li>
                    <li><strong>If any reports have already been finalised, they will need to be re-finalised</strong> to reflect the updated name.</li>
                </ul>
            </WarningPanel>

            <div className="space-y-3">
                {/* First Name */}
                <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">First Name</label>
                    <input
                        type="text"
                        value={editData.firstName}
                        onChange={(e) => setEditData(prev => ({ ...prev, firstName: sanitizeNameInput(e.target.value) }))}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                        placeholder="Enter first name"
                    />
                </div>

                {/* Last Name */}
                <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Last Name</label>
                    <input
                        type="text"
                        value={editData.lastName}
                        onChange={(e) => setEditData(prev => ({ ...prev, lastName: sanitizeNameInput(e.target.value) }))}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                        placeholder="Enter last name"
                    />
                </div>
            </div>

            {/* Action buttons */}
            <div className="flex justify-end space-x-2 pt-2">
                <button
                    onClick={goBackToSummary}
                    className="px-3 py-1.5 text-xs font-semibold text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                >
                    Cancel
                </button>
                <button
                    onClick={() => goToConfirm('edit-name')}
                    disabled={!isNameFormValid() || !computeChangedFields(['firstName', 'lastName'])}
                    className={`px-3 py-1.5 text-xs font-semibold text-white rounded-md transition-colors ${
                        isNameFormValid() && computeChangedFields(['firstName', 'lastName'])
                            ? 'bg-violet-600 hover:bg-violet-700'
                            : 'bg-gray-400 cursor-not-allowed opacity-50'
                    }`}
                >
                    Review Changes
                </button>
            </div>
        </div>
    );

    // ─── Render: Edit Reference Number form ──────────────────────────────────

    const renderEditReference = () => (
        <div className="space-y-3">
            <button
                onClick={goBackToSummary}
                className="text-xs text-violet-600 hover:text-violet-800 font-semibold"
            >
                &larr; Back to Summary
            </button>

            <h3 className="text-base font-semibold text-gray-900">Edit Reference Number</h3>

            {/* Warning about reports */}
            <WarningPanel variant="warning">
                <ul className="list-disc pl-4 space-y-1">
                    <li><strong>If any reports have already been finalised, they will need to be re-finalised</strong> to reflect the updated reference number.</li>
                    <li>Existing exports and downloaded reports will <strong>retain the previous reference number</strong>.</li>
                </ul>
            </WarningPanel>

            <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Reference Number</label>
                <input
                    type="text"
                    value={editData.referenceNumber}
                    onChange={(e) => setEditData(prev => ({ ...prev, referenceNumber: e.target.value }))}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                    placeholder="Enter reference number"
                    maxLength={100}
                />
            </div>

            {/* Action buttons */}
            <div className="flex justify-end space-x-2 pt-2">
                <button
                    onClick={goBackToSummary}
                    className="px-3 py-1.5 text-xs font-semibold text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                >
                    Cancel
                </button>
                <button
                    onClick={() => goToConfirm('edit-reference')}
                    disabled={!isReferenceFormValid() || !computeChangedFields(['referenceNumber'])}
                    className={`px-3 py-1.5 text-xs font-semibold text-white rounded-md transition-colors ${
                        isReferenceFormValid() && computeChangedFields(['referenceNumber'])
                            ? 'bg-violet-600 hover:bg-violet-700'
                            : 'bg-gray-400 cursor-not-allowed opacity-50'
                    }`}
                >
                    Review Changes
                </button>
            </div>
        </div>
    );

    // ─── Render: Edit Email form ────────────────────────────────────────────
    // Only rendered when the patient hasn't set up their password yet.
    // Allows providers to correct a wrongly entered email before the patient
    // begins their assessment, instead of needing to delete and re-add them.

    const renderEditEmail = () => (
        <div className="space-y-3">
            <button
                onClick={goBackToSummary}
                className="text-xs text-violet-600 hover:text-violet-800 font-semibold"
            >
                &larr; Back to Summary
            </button>

            <h3 className="text-base font-semibold text-gray-900">Update Email Address</h3>

            <WarningPanel variant="info">
                <ul className="list-disc pl-4 space-y-1">
                    <li>A <strong>new welcome email</strong> will be sent to the updated address.</li>
                    <li>The patient will need to use the <strong>new email</strong> to sign in and set up their password.</li>
                    <li>Any <strong>previous welcome email links</strong> will stop working.</li>
                </ul>
            </WarningPanel>

            <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Email Address</label>
                <input
                    type="email"
                    value={editData.email}
                    onChange={(e) => setEditData(prev => ({ ...prev, email: sanitizeEmailInput(e.target.value) }))}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                    placeholder="Enter email address"
                    maxLength={254}
                />
                {editData.email?.trim() && !isEmailFormValid() && (
                    <p className="text-xs text-red-500 mt-1">Please enter a valid email address.</p>
                )}
            </div>

            <div className="flex justify-end space-x-2 pt-2">
                <button
                    onClick={goBackToSummary}
                    className="px-3 py-1.5 text-xs font-semibold text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                >
                    Cancel
                </button>
                <button
                    onClick={() => goToConfirm('edit-email')}
                    disabled={!isEmailFormValid() || !computeChangedFields(['email'])}
                    className={`px-3 py-1.5 text-xs font-semibold text-white rounded-md transition-colors ${
                        isEmailFormValid() && computeChangedFields(['email'])
                            ? 'bg-violet-600 hover:bg-violet-700'
                            : 'bg-gray-400 cursor-not-allowed opacity-50'
                    }`}
                >
                    Review Changes
                </button>
            </div>
        </div>
    );

    // ─── Render: Unlock/Re-lock Forms confirmation screen ──────────────────
    // This is a single-step confirm-and-submit screen (no separate "confirm"
    // step) because there is no editable form state — the action is a toggle.

    const renderUnlockForms = () => {
        const unlocking = !isCurrentlyUnlocked;

        return (
            <div className="space-y-3">
                <button
                    onClick={goBackToSummary}
                    className="text-xs text-violet-600 hover:text-violet-800 font-semibold"
                >
                    &larr; Back to Summary
                </button>

                <h3 className="text-base font-semibold text-gray-900">
                    {unlocking ? 'Unlock Questionnaire Forms' : 'Re-lock Questionnaire Forms'}
                </h3>

                <p className="text-xs text-gray-600">
                    {unlocking
                        ? <>This will allow <span className="font-semibold">{patient?.firstName} {patient?.lastName}</span> to edit their questionnaire forms again.</>
                        : <>This will return <span className="font-semibold">{patient?.firstName} {patient?.lastName}</span>'s questionnaire forms to read-only.</>
                    }
                </p>

                {/* Clinical safety warning — different messaging for unlock vs re-lock */}
                {unlocking ? (
                    <WarningPanel variant="warning">
                        <ul className="list-disc pl-4 space-y-1">
                            <li>The patient's answers may change once forms are unlocked.</li>
                            <li>After the patient updates their forms, go to <strong>Manage Documents</strong> and press <strong>"Re-generate outputs"</strong> on the <strong>Clinical Interview</strong> row to refresh the AI-generated paragraphs.</li>
                            <li>You will then need to <strong>review, edit, and re-approve</strong> all affected paragraphs before finalising.</li>
                        </ul>
                    </WarningPanel>
                ) : (
                    <WarningPanel variant="info">
                        <p>The patient will no longer be able to edit their questionnaire forms. They will still be able to view their submitted answers.</p>
                    </WarningPanel>
                )}

                {/* Error message */}
                {submitError && (
                    <div className="bg-red-50 border border-red-200 rounded-md p-3">
                        <p className="text-xs text-red-700">{submitError}</p>
                    </div>
                )}

                {/* Action buttons */}
                <div className="flex justify-end space-x-2 pt-2">
                    <button
                        onClick={goBackToSummary}
                        disabled={isSubmitting}
                        className="px-3 py-1.5 text-xs font-semibold text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleUnlockFormsSubmit}
                        disabled={isSubmitting}
                        className={`px-3 py-1.5 text-xs font-semibold text-white rounded-md transition-colors flex items-center space-x-1 ${
                            isSubmitting
                                ? 'bg-gray-400 cursor-not-allowed opacity-50'
                                : 'bg-violet-600 hover:bg-violet-700'
                        }`}
                    >
                        {isSubmitting && (
                            <svg className="animate-spin h-3 w-3 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                        )}
                        <span>
                            {isSubmitting
                                ? (unlocking ? 'Unlocking...' : 'Re-locking...')
                                : (unlocking ? 'Unlock Forms' : 'Re-lock Forms')
                            }
                        </span>
                    </button>
                </div>
            </div>
        );
    };

    // ─── Render: Confirmation screen ─────────────────────────────────────────

    const renderConfirm = () => {
        // Scope the diff to the fields relevant to the edit origin
        const scopeMap = {
            'edit-reference': ['referenceNumber'],
            'edit-email': ['email'],
        };
        const scope = scopeMap[editOrigin] || ['firstName', 'lastName'];
        const changes = computeChangedFields(scope);
        if (!changes) {
            goBackToSummary();
            return null;
        }

        const hasNameChanges = changes.firstName !== undefined || changes.lastName !== undefined;
        const hasReferenceChanges = changes.referenceNumber !== undefined;
        const hasEmailChanges = changes.email !== undefined;

        // Determine the back-button label and target based on which edit flow
        // brought the user to this screen.
        const backLabelMap = {
            'edit-reference': 'Back to Edit Reference Number',
            'edit-email': 'Back to Update Email Address',
        };
        const backLabel = backLabelMap[editOrigin] || 'Back to Edit Name';

        return (
            <div className="space-y-3">
                <button
                    onClick={() => setCurrentStep(editOrigin || 'edit-name')}
                    className="text-xs text-violet-600 hover:text-violet-800 font-semibold"
                >
                    &larr; {backLabel}
                </button>

                <h3 className="text-base font-semibold text-gray-900">Confirm Changes</h3>

                <p className="text-xs text-gray-600">
                    Please review the following changes for{' '}
                    <span className="font-semibold">
                        {patient?.firstName} {patient?.lastName}
                    </span>
                    :
                </p>

                {/* Diff view: shows old -> new for each changed field */}
                <div className="bg-gray-50 border border-gray-200 rounded-md p-3 space-y-2">
                    {Object.entries(changes).map(([key, newValue]) => (
                        <div key={key} className="text-xs">
                            <span className="font-medium text-gray-700">{FIELD_LABELS[key]}:</span>
                            <div className="flex flex-wrap items-center gap-x-2 mt-0.5 ml-2">
                                <span className="text-red-600 line-through break-all">
                                    {getOriginalValue(key) || '(empty)'}
                                </span>
                                <span className="text-gray-400">&rarr;</span>
                                <span className="text-green-700 font-medium break-all">
                                    {newValue || '(empty)'}
                                </span>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Context-aware warning banners: different messaging
                    depending on whether this is a name, reference, or email change */}
                {hasEmailChanges ? (
                    <WarningPanel variant="info">
                        <ul className="list-disc pl-4 space-y-1">
                            <li>A <strong>new welcome email</strong> will be sent to the updated address.</li>
                            <li>The patient will need to use the <strong>new email</strong> to sign in and set up their password.</li>
                            <li>Any <strong>previous welcome email links</strong> will stop working.</li>
                        </ul>
                    </WarningPanel>
                ) : (
                    <WarningPanel variant="warning">
                        <ul className="list-disc pl-4 space-y-1">
                            {hasNameChanges && (
                                <li>Name changes will be propagated across the platform, including <strong>questionnaire answers</strong>, <strong>informant records</strong>, and <strong>clinician records</strong>. This may take a minute.</li>
                            )}
                            {hasReferenceChanges && (
                                <li>Existing exports and downloaded reports will <strong>retain the previous reference number</strong>.</li>
                            )}
                            <li><strong>If any reports have already been finalised, they will need to be re-finalised</strong> to reflect the updated {hasNameChanges && hasReferenceChanges ? 'details' : hasNameChanges ? 'name' : 'reference number'}.</li>
                        </ul>
                    </WarningPanel>
                )}

                {/* Error message */}
                {submitError && (
                    <div className="bg-red-50 border border-red-200 rounded-md p-3">
                        <p className="text-xs text-red-700">{submitError}</p>
                    </div>
                )}

                {/* Action buttons */}
                <div className="flex justify-end space-x-2 pt-2">
                    <button
                        onClick={goBackToSummary}
                        disabled={isSubmitting}
                        className="px-3 py-1.5 text-xs font-semibold text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                        className={`px-3 py-1.5 text-xs font-semibold text-white rounded-md transition-colors flex items-center space-x-1 ${
                            isSubmitting
                                ? 'bg-gray-400 cursor-not-allowed opacity-50'
                                : 'bg-violet-600 hover:bg-violet-700'
                        }`}
                    >
                        {isSubmitting && (
                            <svg className="animate-spin h-3 w-3 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                        )}
                        <span>{isSubmitting ? 'Saving...' : 'Confirm Changes'}</span>
                    </button>
                </div>
            </div>
        );
    };

    // ─── Render: Success screen ──────────────────────────────────────────────

    const renderSuccess = () => {
        // Contextual success messaging based on what was just changed.
        // editOrigin tracks which flow led here so we can tailor the copy.
        const isEmailUpdate = editOrigin === 'edit-email';
        const isUnlockForms = editOrigin === 'unlock-forms';

        let title = 'Patient Details Updated';
        let successMessage = 'The patient\'s details have been updated successfully. Changes have been propagated to all relevant records across the platform.';

        if (isEmailUpdate) {
            title = 'Email Address Updated';
            successMessage = 'The patient\'s email address has been updated. A welcome email has been sent to the new address.';
        } else if (isUnlockForms) {
            // Use lastUnlockAction (captured at submit time) rather than
            // isCurrentlyUnlocked (derived from the live patient prop).
            // The patient data may have already refreshed via onSuccess, so
            // isCurrentlyUnlocked could reflect the NEW state, not the OLD.
            const justUnlocked = lastUnlockAction === 'unlock';
            title = justUnlocked ? 'Forms Unlocked' : 'Forms Re-locked';
            successMessage = justUnlocked
                ? 'The patient can now edit their questionnaire forms. Remember to re-generate Clinical Interview outputs from Manage Documents once they have finished.'
                : 'The patient\'s questionnaire forms have been returned to read-only.';
        }

        return (
            <div className="space-y-3 text-center">
                <div className="flex justify-center">
                    <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                        <CheckCircleSolid className="h-6 w-6 text-green-600" />
                    </div>
                </div>
                <h3 className="text-base font-semibold text-gray-900">{title}</h3>
                <p className="text-xs text-gray-600">{successMessage}</p>

                {/* Unlock-specific reminder: prompt re-generation after the
                    patient has finished editing their forms. Kept concise to
                    avoid overwhelming the user. */}
                {isUnlockForms && lastUnlockAction === 'unlock' && (
                    <WarningPanel variant="info">
                        <p>The patient may need to refresh their browser session to see the unlocked forms.</p>
                    </WarningPanel>
                )}

                <button
                    onClick={goBackToSummary}
                    className="px-4 py-2 text-xs font-semibold text-white bg-violet-600 rounded-md hover:bg-violet-700 transition-colors"
                >
                    Done
                </button>
            </div>
        );
    };

    // ─── Render: Step router ─────────────────────────────────────────────────

    const renderCurrentStep = () => {
        switch (currentStep) {
            case 'summary':
                return renderSummary();
            case 'edit-name':
                return renderEditName();
            case 'edit-reference':
                return renderEditReference();
            case 'edit-email':
                return renderEditEmail();
            case 'confirm':
                return renderConfirm();
            case 'unlock-forms':
                return renderUnlockForms();
            case 'success':
                return renderSuccess();
            case 'reset-mfa':
                return (
                    <ResetMfaFlow
                        targetUser={{
                            firstName: patient?.firstName || '',
                            lastName: patient?.lastName || '',
                            email: patient?.email || '',
                            isMFASetUp: patient?.isMFASetUp,
                            mfaMethod: patient?.mfaMethod,
                        }}
                        targetUserType="patient"
                        onReset={async () => {
                            setMfaResetLoading(true);
                            setMfaResetError(null);
                            try {
                                const resetMfa = httpsCallable(functions, 'resetUserMfa');
                                const result = await resetMfa({
                                    targetUserId: patientId,
                                    targetUserType: 'patient',
                                });
                                return result.data;
                            } catch (err) {
                                const message = err?.message || 'Failed to reset two-factor authentication. Please try again.';
                                setMfaResetError(message);
                                throw err;
                            } finally {
                                setMfaResetLoading(false);
                            }
                        }}
                        onBack={() => setCurrentStep('summary')}
                        onFinish={() => {
                            onSuccess?.();
                            setCurrentStep('summary');
                        }}
                        actionLoading={mfaResetLoading}
                        actionError={mfaResetError}
                        clearActionState={() => setMfaResetError(null)}
                    />
                );
            default:
                return renderSummary();
        }
    };

    // ─── Modal wrapper ───────────────────────────────────────────────────────

    return (
        <AlertDialog.Root open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
            <AlertDialog.Portal>
                <AlertDialog.Overlay className="radix-dialog-overlay-motion fixed inset-0 bg-black/60 backdrop-blur-sm z-50" />
                <AlertDialog.Content
                    className="radix-dialog-content-centered-motion fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white p-4 md:p-6 rounded-lg shadow-lg max-w-lg w-[calc(100vw-1rem)] max-h-[95vh] overflow-y-auto z-50 transition-all duration-300 ease-in-out"
                >
                    <VisuallyHidden.Root>
                        <AlertDialog.Title>Edit Patient Details</AlertDialog.Title>
                    </VisuallyHidden.Root>
                    <VisuallyHidden.Root>
                        <AlertDialog.Description>
                            Edit the selected patient's details. Changes propagate across the platform.
                        </AlertDialog.Description>
                    </VisuallyHidden.Root>

                    {/* Close button */}
                    <button
                        onClick={onClose}
                        className="absolute top-2 right-2 p-1 rounded-full text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                        aria-label="Close"
                    >
                        <XIcon className="h-5 w-5" />
                    </button>

                    {renderCurrentStep()}
                </AlertDialog.Content>
            </AlertDialog.Portal>
        </AlertDialog.Root>
    );
}

export default ProviderEditPatientForm;
